import { IProduct, ITourFare } from "@interfaces";
import { FARE_PRICE_TRAVEL_DATE_FORMAT } from "@constants";
import _ from "lodash";
import moment from "moment";

/**
 * Determines if a product should be hidden based on business rules
 * @param product - The product to check (may be nested in json property)
 * @returns true if product should be hidden, false if it should be shown
 */
export const shouldHideProduct = (product: IProduct | any): boolean => {
    // Handle products nested in json property
    const actualProduct = product?.json || product;

    // Rule 1: Hide if apiProviderId == 22
    if (actualProduct?.apiProviderId === 22) {
        return true;
    }

    // Rule 2: Hide if productClass == "Y" and departureDates is empty/null/undefined
    if (actualProduct?.productClass === "Y") {
        const departureDates = actualProduct.departureDates;
        if (_.isNil(departureDates) || _.isEmpty(departureDates)) {
            return true;
        }
    }

    return false;
};

/**
 * Filters an array of products, removing those that should be hidden
 * @param products - Array of products to filter
 * @returns Filtered array of products
 */
export const filterProducts = <T extends IProduct | any>(products: T[]): T[] => {
    if (!Array.isArray(products)) {
        return products;
    }
    return products.filter((product) => !shouldHideProduct(product));
};

/**
 * Determines if a single fare has expired based on its travelEnd date.
 * A fare with no travelEnd (open-ended) is never considered expired.
 * @param fare - The fare to check
 * @returns true if the fare's travelEnd is in the past
 */
export const isFareExpired = (fare: Pick<ITourFare, "travelEnd"> | null | undefined): boolean => {
    if (_.isNil(fare) || _.isNil(fare.travelEnd)) {
        return false;
    }
    return moment(fare.travelEnd, FARE_PRICE_TRAVEL_DATE_FORMAT).isBefore(moment());
};

/**
 * Determines if a product is "no longer available" based on its fares.
 * A product is only expired when it has fares and EVERY fare has expired.
 * Products with no fares are not treated as expired (availability is resolved elsewhere).
 * @param fares - The product's fares/prices
 * @returns true if every fare has expired
 */
export const areAllFaresExpired = (
    fares: ReadonlyArray<Pick<ITourFare, "travelEnd">> | null | undefined,
): boolean => {
    if (!Array.isArray(fares) || _.isEmpty(fares)) {
        return false;
    }
    return fares.every(isFareExpired);
};
